import { fetchByIds, fetchOrgMembersWith } from "@/lib/large-org";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Wrench, Trash2, Pencil, QrCode, Printer } from "lucide-react";
import { toast } from "sonner";
import { EquipmentQrDialog } from "@/components/EquipmentQrDialog";
import { BulkQrDialog } from "@/components/BulkQrDialog";

export const Route = createFileRoute("/_authenticated/equipment-admin")({
  component: EquipmentAdminPage,
  head: () => ({ meta: [{ title: "Equipment Admin — MissionReady" }] }),
});

type Equipment = {
  id: string; label: string; category: string | null; serial_number: string | null;
  status: "available" | "checked_out" | "damaged" | "retired";
  assigned_to_user_id: string | null; assigned_to_site_id: string | null;
  notes: string | null; qr_token: string | null;
};
type Site = { id: string; name: string };
type Member = { user_id: string; profile?: { full_name: string | null; email: string | null } | null };

const STATUS_COLOR: Record<Equipment["status"], string> = {
  available: "bg-emerald-600/20 text-emerald-400 border-emerald-500/40",
  checked_out: "bg-amber-600/20 text-amber-300 border-amber-500/40",
  damaged: "bg-[#CC0000]/20 text-[#CC0000] border-[#CC0000]/40",
  retired: "bg-zinc-600/20 text-zinc-300 border-zinc-500/40",
};

function EquipmentAdminPage() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const canEdit = isPlatformAdmin || ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const [items, setItems] = useState<Equipment[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Equipment | null>(null);
  const [form, setForm] = useState<Partial<Equipment>>({ status: "available" });
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [qrItem, setQrItem] = useState<Equipment | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    if (!currentOrg) return;
    const [{ data: e }, { data: s }, { data: m }] = await Promise.all([
      supabase.from("equipment").select("*").eq("organization_id", currentOrg.id).order("label"),
      supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id).order("name"),
      fetchOrgMembersWith(supabase, currentOrg.id).then((d) => ({ data: d })),
    ]);
    setItems((e ?? []) as Equipment[]);
    setSites((s ?? []) as Site[]);
    const ids = (m ?? []).map((x) => x.user_id as string);
    if (ids.length) {
      const { data: profs } = await fetchByIds<any>("profiles", "id,full_name,email", ids, "id", supabase).then((d) => ({ data: d, error: null }));
      const pmap = new Map((profs ?? []).map((p) => [p.id as string, p]));
      setMembers(ids.map((id) => ({ user_id: id, profile: (pmap.get(id) ?? null) as Member["profile"] })));
    } else {
      setMembers([]);
    }
  };
  useEffect(() => { load(); }, [currentOrg?.id]);

  const startCreate = () => { setEditing(null); setForm({ status: "available" }); setOpen(true); };
  const startEdit = (it: Equipment) => { setEditing(it); setForm(it); setOpen(true); };

  const save = async () => {
    if (!currentOrg || !form.label) return toast.error("Label is required");
    const payload = {
      organization_id: currentOrg.id,
      label: form.label, category: form.category ?? null, serial_number: form.serial_number ?? null,
      status: form.status ?? "available",
      assigned_to_user_id: form.assigned_to_user_id ?? null,
      assigned_to_site_id: form.assigned_to_site_id ?? null,
      notes: form.notes ?? null,
    };
    const { error } = editing
      ? await supabase.from("equipment").update(payload).eq("id", editing.id)
      : await supabase.from("equipment").insert(payload);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Updated" : "Added");
    setOpen(false); load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this item?")) return;
    const { error } = await supabase.from("equipment").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted"); load();
  };

  const memberLabel = (uid: string | null) => {
    if (!uid) return "—";
    const m = members.find((x) => x.user_id === uid);
    return m?.profile?.full_name ?? m?.profile?.email ?? uid.slice(0, 6);
  };
  const siteLabel = (sid: string | null) => sites.find((s) => s.id === sid)?.name ?? "—";

  const filtered = statusFilter === "all" ? items : items.filter((i) => i.status === statusFilter);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Equipment Admin</h1>
          <p className="text-sm text-white/60">Manage all equipment</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button onClick={() => setBulkOpen(true)} variant="outline">
              <Printer className="h-4 w-4 mr-1" /> Bulk QR
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={startCreate} className="bg-[#CC0000] hover:bg-[#a30000] text-white">
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Equipment" : "Add Equipment"}</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Label *</Label><Input aria-label="Label *" value={form.label ?? ""} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Category</Label><Input aria-label="Category" value={form.category ?? ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Radio" /></div>
                  <div><Label>Serial Number</Label><Input aria-label="Serial Number" value={form.serial_number ?? ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status ?? "available"} onValueChange={(v) => setForm({ ...form, status: v as Equipment["status"] })}>
                    <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="checked_out">Checked Out</SelectItem>
                      <SelectItem value="damaged">Damaged</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign to Officer</Label>
                  <Select value={form.assigned_to_user_id ?? "none"} onValueChange={(v) => setForm({ ...form, assigned_to_user_id: v === "none" ? null : v })}>
                    <SelectTrigger aria-label="Assign to Officer"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.profile?.full_name ?? m.profile?.email ?? m.user_id.slice(0, 6)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign to Site</Label>
                  <Select value={form.assigned_to_site_id ?? "none"} onValueChange={(v) => setForm({ ...form, assigned_to_site_id: v === "none" ? null : v })}>
                    <SelectTrigger aria-label="Assign to Site"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Input aria-label="Notes" value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={save} className="bg-[#CC0000] hover:bg-[#a30000]">{editing ? "Save" : "Add"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-white/70">Status:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger aria-label="Status:" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="checked_out">Checked Out</SelectItem>
            <SelectItem value="damaged">Damaged</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        {filtered.length === 0 && <p className="text-sm text-white/50">No items.</p>}
        {filtered.map((it) => (
          <div key={it.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="flex items-center gap-3 min-w-0">
              <Wrench className="h-5 w-5 text-[#CC0000] shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-white truncate">{it.label}</div>
                <div className="text-xs text-white/50 truncate">
                  {it.category ?? "—"}{it.serial_number ? ` · SN ${it.serial_number}` : ""} · {memberLabel(it.assigned_to_user_id)} · {siteLabel(it.assigned_to_site_id)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`border ${STATUS_COLOR[it.status]}`}>{it.status.replace("_", " ")}</Badge>
              <Button size="icon" variant="ghost" onClick={() => setQrItem(it)} title="QR code">
                <QrCode className="h-4 w-4" />
              </Button>
              {canEdit && (
                <>
                  <Button size="icon" variant="ghost" onClick={() => startEdit(it)} aria-label={`Edit ${it.label}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(it.id)} aria-label={`Delete ${it.label}`}><Trash2 className="h-4 w-4 text-[#CC0000]" /></Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <EquipmentQrDialog
        open={!!qrItem}
        onOpenChange={(v) => !v && setQrItem(null)}
        token={qrItem?.qr_token ?? null}
        label={qrItem?.label ?? ""}
        serial={qrItem?.serial_number ?? null}
      />
      <BulkQrDialog open={bulkOpen} onOpenChange={setBulkOpen} items={items} />
    </div>
  );
}
